import type { EcosystemItem, ExampleOutcome, OutcomeRow, PipelineStep } from "./types";

// ─── ALL DATA IN THIS FILE IS DEMONSTRATION / CONCEPT DATA ──────────────────
// No figure here represents live protocol statistics, users, or financials.

export const PIPELINE: PipelineStep[] = [
  {
    id: "01",
    name: "ACTION",
    desc: "An actor performs an observable action.",
    detail:
      "Any operation that can be observed — a trade, a deployment, a report, a completed task — enters the protocol with a defined actor and intent.",
    meta: "INPUT · ACTOR + INTENT",
  },
  {
    id: "02",
    name: "CLAIM",
    desc: "A measurable outcome is defined.",
    detail:
      "Before execution, the actor commits to a claim: a specific, measurable result with a baseline and a target. Vague intent cannot produce verified consequence.",
    meta: "SPEC · BASELINE + TARGET",
  },
  {
    id: "03",
    name: "EXECUTE",
    desc: "The actor attempts to produce the outcome.",
    detail:
      "The actor works. Capital, code, or compute is spent attempting to move the metric from baseline toward target.",
    meta: "STATE · IN PROGRESS",
  },
  {
    id: "04",
    name: "OBSERVE",
    desc: "Relevant data is collected.",
    detail:
      "Oracles, on-chain state, and attested sources observe the result. The protocol records what actually happened — not what was reported.",
    meta: "SOURCES · ON-CHAIN + ATTESTED",
  },
  {
    id: "05",
    name: "VERIFY",
    desc: "Evidence is evaluated.",
    detail:
      "Collected evidence is evaluated against the claim’s measurement rules. Verification is deterministic where possible — and disputed where not.",
    meta: "RULES · DETERMINISTIC",
  },
  {
    id: "06",
    name: "CONSEQUENCE",
    desc: "The actual result is measured.",
    detail:
      "The outcome is measured against baseline and target. Verified consequences are recorded permanently; missed claims are recorded just as honestly.",
    meta: "OUTPUT · MEASURED DELTA",
  },
  {
    id: "07",
    name: "REPUTATION",
    desc: "The verified result becomes part of the actor’s track record.",
    detail:
      "Every verified consequence compounds into an actor’s track record — the asset that determines trust, opportunity, and economic access.",
    meta: "ASSET · TRACK RECORD",
  },
];

export const PROTOCOL_NODES: PipelineStep[] = [
  {
    id: "01",
    name: "ACTION",
    desc: "Actors enter the protocol through observable actions.",
    detail:
      "The intake layer. Human workers, AI agents, protocols, and autonomous businesses all generate actions that can be anchored to the Consequence Layer.",
    meta: "LAYER 01 · INTAKE",
  },
  {
    id: "02",
    name: "CLAIM",
    desc: "Actions are bound to measurable outcome commitments.",
    detail:
      "An Outcome Contract specifies objective, baseline, target, measurement, deadline, and stake — turning intent into a falsifiable statement.",
    meta: "LAYER 02 · INTENT",
  },
  {
    id: "03",
    name: "OUTCOME",
    desc: "The observable result the claim points at.",
    detail:
      "Outcomes are the protocol’s atomic unit: a measurable state of the world the claim predicted or attempted to produce.",
    meta: "LAYER 03 · MEASUREMENT",
  },
  {
    id: "04",
    name: "EVIDENCE",
    desc: "Data that proves what the outcome actually was.",
    detail:
      "On-chain state, oracle feeds, attested reports, and cryptographic proofs. Evidence is what separates consequence from narrative.",
    meta: "LAYER 04 · PROOF",
  },
  {
    id: "05",
    name: "VERIFICATION",
    desc: "Evidence is evaluated against the claim.",
    detail:
      "Deterministic checks where possible, dispute resolution where not. Verification produces a verdict the network can agree on.",
    meta: "LAYER 05 · CONSENSUS",
  },
  {
    id: "06",
    name: "CONSEQUENCE",
    desc: "Verified results settle into permanent records.",
    detail:
      "Settlement records the measured delta — positive, negative, or missed — and triggers the stake and reward logic of the contract.",
    meta: "LAYER 06 · SETTLEMENT",
  },
  {
    id: "07",
    name: "REPUTATION",
    desc: "Consequences compound into track records.",
    detail:
      "The Consequence Score aggregates verified history into a portable reputation primitive that markets can price and protocols can trust.",
    meta: "LAYER 07 · IDENTITY",
  },
];

export const STORY_FLOW = ["ACTION", "OUTCOME", "VERIFICATION", "REPUTATION", "ECONOMIC ACCESS"];

export const ACTIVITY_EXAMPLES = [
  { value: 10000, suffix: "", label: "transactions" },
  { value: 100, suffix: "", label: "commits" },
  { value: 50, suffix: "", label: "completed tasks" },
  { value: 1000000, suffix: "", label: "impressions" },
];

export const EXAMPLE_OUTCOMES: ExampleOutcome[] = [
  {
    id: "EX-001",
    actorType: "AI AGENT",
    actorId: "AGENT #0471",
    claim: "“I can increase protocol revenue by 15%.”",
    rows: [
      { label: "BASELINE", value: "$100,000" },
      { label: "TARGET", value: "$115,000" },
      { label: "ACTUAL", value: "$127,400" },
    ],
    result: "VERIFIED",
    resultTone: "success",
    consequence: "+27.4%",
  },
  {
    id: "EX-002",
    actorType: "DEVELOPER",
    actorId: "DEV #0119",
    claim: "“I can reduce transaction costs by 30%.”",
    rows: [
      { label: "BASELINE", value: "$1.00" },
      { label: "TARGET", value: "$0.70" },
      { label: "ACTUAL", value: "$0.63" },
    ],
    result: "VERIFIED",
    resultTone: "success",
    consequence: "−37%",
  },
  {
    id: "EX-003",
    actorType: "RESEARCHER",
    actorId: "RESEARCHER #021",
    claim: "“I can identify a critical vulnerability.”",
    rows: [
      { label: "EVIDENCE", value: "Verified security report" },
      { label: "SEVERITY", value: "CRITICAL" },
      { label: "ACTUAL", value: "0 days to patch" },
    ],
    result: "CONFIRMED",
    resultTone: "info",
    consequence: "CRITICAL RISK PREVENTED",
  },
];

export const OUTCOMES: OutcomeRow[] = [
  { id: "OC-0471", actor: "AGENT #0471", objective: "Revenue Growth", target: "+15%", actual: "+27.4%", impact: 27.4, impactLabel: "+27.4%", status: "VERIFIED", epoch: "E-0471", evidence: "0x7f3a…c21e", opened: "T-30D" },
  { id: "OC-0922", actor: "DEV #0119", objective: "Gas Reduction", target: "−30%", actual: "−37%", impact: 37, impactLabel: "−37%", status: "VERIFIED", epoch: "E-0468", evidence: "0x2b91…aa04", opened: "T-30D" },
  { id: "OC-0999", actor: "DEV #0233", objective: "Bridge Latency", target: "−45%", actual: "PENDING", impact: 0, impactLabel: "—", status: "ACTIVE", epoch: "E-0472", evidence: "0x9c04…71bd", opened: "T-12D" },
  { id: "OC-0318", actor: "TRADER #044", objective: "Slippage Control", target: "−12%", actual: "−14.2%", impact: 14.2, impactLabel: "−14.2%", status: "VERIFIED", epoch: "E-0465", evidence: "0x41dd…0e9a", opened: "T-21D" },
  { id: "OC-0555", actor: "AGENT #201", objective: "Liquidity Expansion", target: "+25%", actual: "+9.8%", impact: 9.8, impactLabel: "+9.8%", status: "FAILED", epoch: "E-0461", evidence: "0xe8c2…33f7", opened: "T-30D" },
  { id: "OC-0745", actor: "PROTOCOL #007", objective: "Uptime SLA", target: "99.9%", actual: "99.94%", impact: 0.04, impactLabel: "+0.04%", status: "VERIFIED", epoch: "E-0469", evidence: "0x08af…d512", opened: "T-14D" },
  { id: "OC-0813", actor: "DAO #012", objective: "Treasury Diversification", target: "40%", actual: "IN DISPUTE", impact: 0, impactLabel: "—", status: "DISPUTED", epoch: "E-0470", evidence: "0x63b7…98c0", opened: "T-18D" },
  { id: "OC-1002", actor: "AGENT #310", objective: "Content Quality Index", target: "+20%", actual: "PENDING", impact: 0, impactLabel: "—", status: "ACTIVE", epoch: "E-0473", evidence: "0xd1f6…5a23", opened: "T-06D" },
  { id: "OC-0607", actor: "RESEARCHER #021", objective: "Critical Vulnerability Disclosure", target: "1 CRITICAL", actual: "1 CRITICAL", impact: 99, impactLabel: "CRITICAL", status: "VERIFIED", epoch: "E-0463", evidence: "0x77aa…41e6", opened: "T-25D" },
];

export const AGENT_PROFILE = {
  id: "AGENT #0471",
  type: "AUTONOMOUS AGENT · CONCEPT PROFILE",
  score: 94.7,
  stats: [
    { label: "VERIFIED OUTCOMES", value: "1,691" },
    { label: "SUCCESS RATE", value: "91.8%" },
    { label: "ECONOMIC IMPACT", value: "$18.2M" },
    { label: "DISPUTES", value: "7" },
    { label: "RESOLVED", value: "7" },
    { label: "TRACK RECORD", value: "ACTIVE" },
  ],
  outcomeHistory: [62, 88, 74, 96, 110, 92, 128, 141, 133, 158, 172, 196],
  successRate: [88.2, 89.1, 90.4, 88.9, 91.2, 92.6, 91.8, 93.0, 92.2, 93.4, 92.8, 91.8],
  impactSeries: [0.4, 0.9, 1.4, 2.2, 3.1, 4.4, 5.9, 7.2, 9.0, 11.6, 14.8, 18.2],
  consistency: [
    { label: "TRADING", value: 92 },
    { label: "LIQUIDITY", value: 88 },
    { label: "REPORTING", value: 97 },
    { label: "EXECUTION", value: 90 },
    { label: "DISPUTES", value: 100 },
  ],
  verifications: [
    { epoch: "E-0471", event: "Revenue growth claim verified", status: "VERIFIED" },
    { epoch: "E-0469", event: "Evidence bundle accepted (oracle + on-chain)", status: "VERIFIED" },
    { epoch: "E-0466", event: "Claim amended before deadline", status: "AMENDED" },
    { epoch: "E-0461", event: "Liquidity claim missed target", status: "FAILED" },
    { epoch: "E-0457", event: "Dispute resolved in actor favor", status: "RESOLVED" },
  ],
};

export const ECOSYSTEM: EcosystemItem[] = [
  {
    id: "ai-agents",
    name: "AI AGENTS",
    prove: "Revenue growth, cost reduction, task completion — with receipts.",
    measure: "Actual delta produced against the agent’s stated claim.",
    reputation: "A portable Consequence Score markets can price and humans can trust.",
  },
  {
    id: "developers",
    name: "DEVELOPERS",
    prove: "Shipping that measurably improved the system, not just merged.",
    measure: "Gas saved, latency reduced, uptime delivered, incidents prevented.",
    reputation: "A verifiable engineering track record across every repo and runtime.",
  },
  {
    id: "traders",
    name: "TRADERS",
    prove: "Risk-adjusted performance claimed before it happened.",
    measure: "PnL against baseline, drawdown discipline, slippage control.",
    reputation: "Strategy credibility that survives beyond a lucky streak.",
  },
  {
    id: "researchers",
    name: "RESEARCHERS",
    prove: "Findings that were real, reproducible, and acted upon.",
    measure: "Vulnerabilities confirmed, reports verified, predictions landed.",
    reputation: "Standing earned by confirmed work instead of citation counts.",
  },
  {
    id: "protocols",
    name: "PROTOCOLS",
    prove: "SLAs, upgrades, and guarantees that held under measurement.",
    measure: "Uptime, throughput, cost targets delivered contract-to-contract.",
    reputation: "Institutional trust built on settled consequences, not promises.",
  },
  {
    id: "autonomous-businesses",
    name: "AUTONOMOUS BUSINESSES",
    prove: "Operations that produced the outcomes they sold.",
    measure: "Revenue, fulfillment, and service levels against committed targets.",
    reputation: "Creditworthiness derived from verified operating history.",
  },
  {
    id: "daos",
    name: "DAOs",
    prove: "Governance decisions that produced measurable results.",
    measure: "Proposal outcomes tracked from vote to verified consequence.",
    reputation: "Delegate and treasury accountability backed by evidence.",
  },
  {
    id: "service-providers",
    name: "SERVICE PROVIDERS",
    prove: "Work delivered against a defined, measurable scope.",
    measure: "Contract completion, quality bars met, deadlines held.",
    reputation: "A public record that wins the next contract on proof.",
  },
];

export const CA_NOT_LIVE_TOAST = "$CONSEQ contract address is not live yet.";
